import React from "react";
import { Button, Popconfirm } from "antd";
import { WarningOutlined, ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { usePeerConnection } from "features/robot-teleop/hooks/usePeerConnection";
import { usePeerStats } from "features/robot-teleop/hooks/usePeerStats";
import { PEER_STATES } from "features/robot-teleop/webrtc/peerStates";
import { stallCause } from "./linkPresentation";

/**
 * PeerStallOverlay — shown over a camera tile ONLY while the link is CONNECTED
 * but no video frames are decoded ('stalled') or no video track was negotiated
 * ('no-track'): red inset ring, a scrim that dims the frozen last frame, the
 * cause and a Reconnect button (confirmed first: it drops the live link).
 * Renders nothing otherwise.
 */
const PeerStallOverlay = ({ peerId }) => {
  const { t } = useTranslation();
  const { state, connectionState, reconnect, isConnecting } = usePeerConnection(peerId);
  const { health, sample, healthSince } = usePeerStats(peerId, { historySize: 2 });

  if (state !== PEER_STATES.CONNECTED) return null;
  if (health !== "stalled" && health !== "no-track") return null;

  const seconds = healthSince ? Math.max(0, Math.round((Date.now() - healthSince) / 1000)) : null;
  const headline = health === "no-track"
    ? t("teleop.peer.stall.noVideoTrack")
    : [t("teleop.peer.stall.noVideoFrames"), seconds != null ? t("teleop.peer.stall.seconds", { n: seconds }) : null]
        .filter(Boolean).join(" · ");

  return (
    <div className="peer-stall-overlay" data-health={health}>
      <div className="peer-stall-card">
        <div className="headline"><WarningOutlined /> {headline}</div>
        <div className="cause">
          {stallCause(health, sample, t)} · {t("teleop.peer.stall.link", { state: connectionState || "connected" })}
        </div>
        <div className="actions">
          <Popconfirm
            title={t("teleop.peer.controls.reconnectTitle", { peerId })}
            description={t("teleop.peer.controls.reconnectDescription")}
            okText={t("teleop.peer.controls.yesReconnect")}
            cancelText={t("teleop.peer.controls.cancel")}
            placement="bottom"
            disabled={isConnecting}
            destroyOnHidden
            onConfirm={reconnect}
          >
            <Button size="small" danger icon={<ReloadOutlined />} disabled={isConnecting}>
              {t("teleop.peer.controls.reconnect")}
            </Button>
          </Popconfirm>
        </div>
      </div>
    </div>
  );
};

export default React.memo(PeerStallOverlay);
